"use client";

import { useState } from "react";
import { Minus, Plus } from "lucide-react";

type Props = { defaultDetailed: boolean };

/**
 * "Expand All / Collapse All" control for the itinerary accordion — matches
 * production's real markup exactly (confirmed via the live DOM): a single
 * full-width button at the top of the accordion card with a circular
 * plus/minus icon on the right, not the Outline/Detailed pill pair the
 * previous build rendered.
 *
 * The day content is server-rendered (see ItineraryTimeline); this island
 * carries no content — it just flips the native `<details open>` on every
 * day via the DOM, so toggling costs nothing and nothing is serialized.
 */
export function ItineraryViewToggle({ defaultDetailed }: Props) {
  const [expanded, setExpanded] = useState(defaultDetailed);

  const apply = (next: boolean) => {
    setExpanded(next);
    document
      .querySelector("[data-itinerary]")
      ?.querySelectorAll<HTMLDetailsElement>("[data-day-details]")
      .forEach((el) => {
        el.open = next;
      });
  };

  return (
    <button
      type="button"
      onClick={() => apply(!expanded)}
      aria-expanded={expanded}
      className="flex w-full flex-wrap items-center gap-2 px-6 py-6 text-base font-bold text-headings hover:underline"
    >
      <span>Expand All / Collapse All</span>
      <span className="ml-auto h-5 w-5 rounded-full bg-primary p-0.5 text-white shadow-md shadow-primary/10">
        {expanded ? (
          <Minus className="h-full w-full" aria-hidden />
        ) : (
          <Plus className="h-full w-full" aria-hidden />
        )}
      </span>
    </button>
  );
}
